import { Component, inject, OnInit, ViewEncapsulation }            from '@angular/core';
import { HttpErrorResponse }                                       from '@angular/common/http';
import { ActivatedRoute, Router }                                  from '@angular/router';
import { MatDialog }                                               from '@angular/material/dialog';
import { MatCard }                                                 from '@angular/material/card';
import { MatProgressSpinner }                                      from '@angular/material/progress-spinner';
import { map, mergeMap, tap }                                      from 'rxjs/operators';
import { EMPTY, Observable, of }                                   from 'rxjs';

import { InternalRoutes }           from '../../../shared/data/internal-routes';
import { ConfirmDialogComponent }   from '../../../shared/components/dialog/cancel-confirm/confirm-dialog.component';
import { UserService }              from '../../../api/user/user.service';
import { AuthenticationService }    from '../../../core/auth/authentication.service';
import { ActionLog, LoggingUtils }  from '../../../shared/utils/logging.utils';
import { MfaOptions }               from '../../../shared/data/mfa-options';
import { SessionService }           from '../../../api/session/session.service';
import { PinNotificationComponent } from '../../../shared/components/notification/notification.component';
import { ButtonComponent }          from '../../../shared/components/buttons/button.component';
import { PingOneIdentityService }   from '../../../core/aws-cognito/ping-one-identity.service';

@Component({
  selector: 'pin-setup-security-token',
  templateUrl: './setup-security-token.component.html',
  styleUrls: ['./setup-security-token.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [PinNotificationComponent, MatCard, ButtonComponent, MatProgressSpinner]
})
export class SetupSecurityTokenComponent implements OnInit {
  isLoading = false;
  comesFromMyProfile = false;
  displayWarningMsg = false;
  currentQueryParams = {};
  displayCancelButton: boolean;
  username: string;
  token: string;
  private authService = inject(AuthenticationService);
  private vendorAuthService = inject(PingOneIdentityService);
  private loggingUtil = inject(LoggingUtils);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private userService = inject(UserService);
  private sessionService = inject(SessionService);
  private returnUrl: string;

  ngOnInit() {
    const queryParams = this.route.snapshot.queryParamMap;
    const userMfaOptions = this.route.snapshot.data.userMfaOptions;

    if (queryParams.has('setupEmail')) {
      this.useEmail();
    }

    this.returnUrl = queryParams.has('returnUrl') ? queryParams.get('returnUrl') : '/';
    this.displayWarningMsg = queryParams.has('warningMsg');
    if (this.returnUrl === InternalRoutes.MY_PROFILE) {
      this.comesFromMyProfile = true;
    }
    this.username = queryParams.get('username');

    this.currentQueryParams = {returnUrl: this.returnUrl, username: this.username};

    this.displayCancelButton = this.comesFromMyProfile && userMfaOptions instanceof Array && userMfaOptions.length > 0;
  }

  useEmail() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    let navigated = false;
    this.resetMfaMethod()
      .pipe(
        mergeMap(_ => this.authService.getUserSessionToken()),
        tap(token => this.token = token),
        mergeMap(_ => this.authService.notifyEmailMfaSetup()),
        mergeMap(_ => this.authService.associateEmailToken()))
      .subscribe({
        next: _ => {
          navigated = true;
          this.blackListOldJwt(this.token);
          this.router.navigate([InternalRoutes.ENTER_VERIFICATION_CODE],
            {
              queryParams: {
                mfaOption: MfaOptions.email_mfa,
                setup: true,
                returnUrl: this.returnUrl
              },
              skipLocationChange: true,
            });
        },
        error: error => {
          this.isLoading = false;
          this.handleError(error, this.token);
        },
        complete: () => {
          if (!navigated) {
            this.isLoading = false;
          }
        }
      });
  }

  handleError(error: HttpErrorResponse, token: string) {
    if (error?.error?.message === 'ERR_LIMIT_EXCEEDED') {
      this.blackListOldJwt(token);
      this.router.navigate([InternalRoutes.ENTER_VERIFICATION_CODE],
        {
          queryParams: {
            mfaOption: MfaOptions.email_mfa,
            tooManyAttempts: true,
            setup: true,
            returnUrl: this.returnUrl
          },
          skipLocationChange: true,
        });
    }
  }

  useMobileApp() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.selectMfaMethod(InternalRoutes.SET_APP_MFA);
  }

  skipMfaSetup(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    const wasOverrideDisabledOptionUsed = this.route.snapshot.queryParamMap.has('overrideDisabledOption');
    this.authService.notifyMfaSetup().subscribe({
      next: () => {
        if (wasOverrideDisabledOptionUsed) {
          this.router
            .navigate([this.returnUrl], {queryParams: {overrideDisabledOption: true}})
            .then(() => this.isLoading = false);
        } else {
          this.router
            .navigate([this.returnUrl])
            .then(() => this.isLoading = false);
        }
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private blackListOldJwt(token: string) {
    this.userService.revokeJWTBySignature(token).subscribe();
  }

  private resetMfaMethod(): Observable<string> {
    return this.authService.getUserMfaOptions()
      .pipe(
        map(mfaOptions => mfaOptions && mfaOptions.length > 0),
        mergeMap(hasAlreadyMfaSet => this.displayConfirmationDialogIfNecessary(hasAlreadyMfaSet)),
        mergeMap(hasAlreadyMfaSet => hasAlreadyMfaSet ? this.disableExistingMfa() : of('SUCCESS'))
      );
  }

  private selectMfaMethod(mfaRoute: string): void {
    let navigated = false;
    this.resetMfaMethod().subscribe({
      next: _ => {
        navigated = true;
        this.router.navigate([mfaRoute], {queryParams: this.currentQueryParams, replaceUrl: true}).then();
      },
      error: () => {
        this.isLoading = false;
      },
      complete: () => {
        if (!navigated) {
          this.isLoading = false;
        }
      }
    });
  }

  private displayConfirmationDialogIfNecessary(shouldDisplay: boolean): Observable<boolean> {
    if (shouldDisplay) {
      return this.dialog.open(ConfirmDialogComponent, {
        width: '740px',
        maxWidth: '95vw',
        disableClose: true,
        panelClass: 'pin-confirm-dialog',
        data: {
          title: 'Update Second Factor Authentication Preference',
          subheading: 'Are you sure you want to update your second factor authentication method? Doing so will remove current settings and a new preference will need to be' +
            ' established before viewing account information.'
        }
      }).afterClosed()
        .pipe(mergeMap(closeType => {
            if (closeType === 'confirm') {
              this.sessionService.loadSession().subscribe(() => {});
              this.currentQueryParams = {returnUrl: this.returnUrl, overrideDisabledOption: true};
              return of(shouldDisplay);
            }
            return EMPTY;
          }
        ));
    } else {
      return of(shouldDisplay);
    }
  }

  private disableExistingMfa(): Observable<string> {
    this.vendorAuthService.getUserMfaOptions()
      .pipe(
        mergeMap((res) => this.loggingUtil.logUserAction(ActionLog.MFA_RESET, res[0])))
      .subscribe();

    return this.authService.disableMfa();
  }
}
